import React, { useState, useEffect, useRef } from "react";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { getMentionConfig, getRelativeTime } from "../helpers/helper";
import "./ReplySection.scss";
import { IoIosMore } from "react-icons/io";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import Toast from "../../components/Toast/Toast";
import ReportDialog from "../Components/ReportDialog/ReportDialog";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { convertToMarkdown, convertMarkdownToHtml } from "../helpers/helper";
import PostContent from "../Post/PostContent";
import AddComments from "../AddComment/AddComment";
import "../AddComment/AddComment.scss";
import { CommentsService } from "../../services";
import { useUser } from "../../hooks/useUser";
import { fetchUsers } from "../../utils/userSuggest";

const ReplySection = ({
  postId,
  commentId,
  showReply,
  replies = [],
  onAddReply,
  currentUser,
  isCommentingBlocked,
  isLoading = false,
  repliesCount,
  addMoreReplies
}) => {
  const [replyText, setReplyText] = useState("");
  const [activeOptionsMenu, setActiveOptionsMenu] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [replyToReport, setReplyToReport] = useState(null);
  const [isReporting, setIsReporting] = useState(false);
  const [isCheckingReport, setIsCheckingReport] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [editingReply, setEditingReply] = useState(null);
  const [editText, setEditText] = useState("");
  const [showEditor, setShowEditor] = useState({}); // Change showEditor to an object to allow multiple editors open at once
  const [mediaModal, setMediaModal] = useState({ open: false, media: null, type: null });
  const menuRef = useRef(null);
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  // Start at 2 if initial replies are from page 1
  const [replyPage, setReplyPage] = useState(2);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const {users} = useUser();
  const currentMentionListRef = useRef([]);
  const lastEmptySearchRef = useRef("");

  // Reset display count and editor when toggling reply section
  useEffect(() => {
    if (!showReply) {
      setShowEditor({});

      // Clean up the editor when hiding reply section
      if (quillRef.current) {
        try {
          // Remove all event handlers
          if (quillRef.current.emitter) {
            quillRef.current.emitter.removeAllListeners();
          }
          quillRef.current = null;
        } catch (err) {
          console.warn('Error cleaning up Quill:', err);
        }

        // Clear the editor container
        if (editorRef.current) {
          while (editorRef.current.firstChild) {
            editorRef.current.removeChild(editorRef.current.firstChild);
          }
        }
      }
    }
  }, [showReply]);

  // Initialize Quill editor when showing it
  useEffect(() => {
    if (showEditor && editorRef.current) {
      // Make sure we have a unique ID for this editor element
      const editorId = `reply-editor-${commentId}`;
      editorRef.current.id = editorId;

      // Check if this specific editor already has a Quill instance
      if (document.querySelector(`#${editorId} .ql-editor`)) {
        return; // Already initialized
      }

      // Clear any existing editor elements first
      while (editorRef.current.firstChild) {
        editorRef.current.removeChild(editorRef.current.firstChild);
      }

      const toolbarOptions = [
        ['bold', 'italic', 'underline', 'strike'],
        ['link'],
        ['clean'],
      ];

      const quill = new Quill(editorRef.current, {
        modules: {
          formats: ["bold", "italic", "mention"],
          toolbar: toolbarOptions,
          mention: getMentionConfig({ currentMentionListRef, fetchUsers, lastEmptySearchRef }),
        },
        formats: ["bold", "italic", "mention"],
        placeholder: "Write a reply...",
        theme: 'snow'
      });

      // Store the instance
      quillRef.current = quill;

      if (replyText) {
        quill.root.innerHTML = replyText;
      }

      quill.on('text-change', () => {
        // Check if quill is still valid before accessing its properties
        if (quill && quill.root) {
          const text = quill.root.innerHTML;
          setReplyText(convertToMarkdown(text));
        }
      });

      quill.focus();
    }

    return () => {
      // We'll handle cleanup in the showReply effect and unmount effect
    };
  }, [showEditor, commentId, replyText]);

  // Final cleanup on unmount
  useEffect(() => {
    return () => {
      if (quillRef.current) {
        try {
          // Remove all event handlers
          if (quillRef.current.emitter) {
            quillRef.current.emitter.removeAllListeners();
          }
          quillRef.current = null;
        } catch (err) {
          console.warn('Error cleaning up Quill:', err);
        }
      }
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveOptionsMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle options menu for a specific reply
  const toggleOptionsMenu = (replyId) => {
    setActiveOptionsMenu(activeOptionsMenu === replyId ? null : replyId);
  };

  // Function to handle reply reporting - Check before showing dialog
  const handleReportReply = async (replyId) => {
    setIsCheckingReport(true);
    setActiveOptionsMenu(null);
    setReplyToReport(replyId);
    setShowReportDialog(true);
    setIsCheckingReport(false);
  };

  // Function to handle report submission from dialog
  const handleReportSubmit = async (reason) => {
    if (!replyToReport) return;
    try {
      setIsReporting(true);
      const response = await CommentsService.reportComment(replyToReport, reason);
      setShowReportDialog(false);
      setReplyToReport(null);

      if (response?.data?.success) {
        setToast({
          show: true,
          message: "Reply has been reported successfully",
          type: "success"
        });
      } else {
        setToast({
          show: true,
          message: response?.data?.message || "Failed to report reply. Please try again.",
          type: "error"
        });
      }
    } catch (error) {
      if (error.response.data?.alreadyReported) {
        setToast({
          show: true,
          message: "You have already reported this reply.",
          type: "warning"
        });
      } else {
        setToast({
          show: true,
          message: "Failed to report reply. Please try again.",
          type: "error"
        });
      }
    } finally {
      setIsReporting(false);
    }
  };

  // Add reply to the database
  const addReply = async (media) => {
    if (!replyText.trim() && !media?.image && !media?.gif) return;

    try {

      const replyData = {
        post_id: postId,
        user_id: currentUser,
        content: replyText,
        created_at: new Date().toISOString(),
        ...(media?.image && { image: media.image }),
        ...(media?.gif && { gif: media.gif }),
        comment_id: commentId
      };
      const response = await CommentsService.createComment(replyData);
      const replyId = response.data.data.id;

      // Pass the data to parent component
      onAddReply(commentId, replyId, {
        ...replyData,
        user: {
          user_id: Number(currentUser),
          user_name: users[currentUser]?.user_name || "You",
          user_image: users[currentUser]?.user_image,
        },
        image_url: replyData.image ? replyData.image.url : undefined,
        gif_url: replyData.gif ? replyData.gif.url : undefined,
      });

      // Reset form
      setReplyText("");
      setShowEditor(false);

      // Clean up Quill instance
      if (quillRef.current) {
        try {
          // Remove all event handlers
          if (quillRef.current.emitter) {
            quillRef.current.emitter.removeAllListeners();
          }
          quillRef.current = null;
        } catch (err) {
          console.warn('Error cleaning up Quill:', err);
        }

        // Clear the editor container
        if (editorRef.current) {
          while (editorRef.current.firstChild) {
            editorRef.current.removeChild(editorRef.current.firstChild);
          }
        }
      }
    } catch (error) {
      console.error("Error adding reply:", error);
    }
  };

  // Start editing a reply
  const startEditingReply = (reply) => {
    setEditingReply(reply.id);
    setEditText(reply.content);
    setActiveOptionsMenu(null);
  };

  // Cancel editing a reply
  const cancelEditingReply = () => {
    setEditingReply(null);
    setEditText("");
  };

  // Save edited reply - only content editing allowed
  const saveEditedReply = async (mediaData = {}) => {
    // Get content from mediaData.text (from Quill editor) or fallback to editText
    const contentToSave = mediaData?.text !== undefined ? mediaData.text : editText;
    if (!contentToSave.trim() || !editingReply) return;

    try {
      
      // Find the original reply to preserve existing media
      const originalReply = replies.find(reply => reply.id === editingReply);
      
      const updateData = {
        content: contentToSave,
        post_id: postId
      };

      const response = await CommentsService.updateComment(editingReply, updateData);

      if (response?.data?.success) {
       // Update reply in UI with the markdown content, keep existing media
        onAddReply(commentId, editingReply, null, (prevReplies) => {
          if (!Array.isArray(prevReplies)) return prevReplies;
          return prevReplies.map((reply) =>
            reply.id === editingReply
              ? {
                  ...reply,
                  content: contentToSave,
                  // Keep existing media unchanged
                  image_url: originalReply?.image_url || null,
                  gif_url: originalReply?.gif_url || null
                }
              : reply
          );
        });

        setEditingReply(null);
        setEditText("");
        setToast({
          show: true,
          message: "Reply updated successfully",
          type: "success"
        });
      } else {
        setToast({
          show: true,
          message: "Failed to update reply",
          type: "error"
        });
      }
    } catch (error) {
      console.error("Error updating reply:", error);
      setToast({
        show: true,
        message: "Error updating reply",
        type: "error"
      });
    }
  };

  // Delete reply using CommentsService and update UI
  const deleteReply = async (replyId) => {
    try {
      const response = await CommentsService.deleteComment(replyId, postId);
      if (response?.data?.success) {
        // Remove reply from UI and decrement replies_count in parent comment
        onAddReply(commentId, replyId, null, (prevReplies, parentComment) => {
          let newReplies = Array.isArray(prevReplies) ? prevReplies.filter((reply) => reply.id !== replyId) : prevReplies;
          // Decrement replies_count in parent comment if possible
          if (parentComment && typeof parentComment.replies_count === 'number') {
            parentComment.replies_count = Math.max(0, parentComment.replies_count - 1);
          }
          return newReplies;
        });
        setToast({
          show: true,
          message: "Reply deleted successfully",
          type: "success"
        });
      } else {
        setToast({
          show: true,
          message: "Failed to delete reply",
          type: "error"
        });
      }
    } catch (error) {
      console.error("Error deleting reply:", error);
      setToast({
        show: true,
        message: "Error deleting reply",
        type: "error"
      });
    }
  };

  // Toggle like/unlike on a reply
  const toggleLikeReply = async (replyId, isLiked) => {
    try {
      // Call backend service to toggle like
      await CommentsService.toggleCommentLike(replyId, isLiked ? 0 : 1);
      // Update UI immediately: update the reply in the replies array
      onAddReply(commentId, replyId, null, (prevReplies) => {
        if (!Array.isArray(prevReplies)) return prevReplies;
        return prevReplies.map((reply) =>
          reply.id === replyId
            ? {
              ...reply,
              user_has_liked: !isLiked,
              likes_count: reply.user_has_liked ? Number(reply.likes_count) - 1 : Number(reply.likes_count) + 1
            }
            : reply
        );
      });
    } catch (error) {
      console.error("Error toggling like on reply:", error);
    }
  };

  // Load more replies - just increase the display count since data is already available
  const loadMoreReplies = async () => {
    setIsLoadingMore(true);
    try {
      const response = await CommentsService.getCommentReplies(commentId, { page: replyPage, sort: "Latest" });
      if (response.data.success) {
        const newReplies = response.data.data;
        addMoreReplies(commentId, newReplies);
        setReplyPage((prev) => prev + 1);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSubmit = (media) => {
    addReply(media);
  };

  const handleInputFocus = (id) => {
    // Delay showing the editor slightly to avoid conflicts with other editors
    setTimeout(() => {
      setShowEditor(prev => ({ ...prev, [id]: true }));
    }, 50);
  };


  const handleCancel = (id) => {
    setShowEditor(prev => ({ ...prev, [id]: false }));
    setReplyText("");
    if (quillRef.current) {
      try {
        if (quillRef.current.root) {
          quillRef.current.root.innerHTML = '';
        }
      } catch (err) {
        console.warn('Error resetting Quill editor:', err);
      }
    }
  };

  if (!showReply) return null;


  // Check if there are more replies to load
  const hasMoreReplies = replies?.length < Number(repliesCount);

  return (
    <>
      <div className="reply-section">
        {isLoading ? (
          <div className="loading-replies">Loading replies</div>
        ) : replies.length ? (
          <div className="reply-list">
            {replies.map((reply) => {
              return (
                <div key={reply.id} className="reply-item-container">
                  <div className="reply-item">
                    <div className="reply-header">
                      <div className="reply-avatar">
                        <img
                          src={reply.user?.user_image || "https://gizmeon.mdc.akamaized.net/PUB-50119/post/1749720612786.png"}
                          alt={reply.user?.user_name || "User"}
                        />
                      </div>
                      <div className="reply-author-info">
                        <span className="reply-author">{reply.user?.user_name || "Unknown User"}</span>
                      </div>
                      <div className="reply-meta">
                        <span className="reply-time">
                          {getRelativeTime(new Date(reply.created_at))}
                        </span>
                        <div className="options-wrapper" ref={activeOptionsMenu === reply.id ? menuRef : null}>
                          <IoIosMore
                            className="more-icon"
                            onClick={() => toggleOptionsMenu(reply.id)}
                          />
                          {activeOptionsMenu === reply.id && (
                            <div className="options-menu">
                              {reply.user?.user_id === Number(currentUser) ? (
                                <>
                                  <button onClick={() => startEditingReply(reply)}>
                                    <FiEdit2 className="comment-icon" /> Edit
                                  </button>
                                  <button onClick={() => deleteReply(reply.id)}>
                                    <RiDeleteBin6Line   className="comment-icon" /> Delete
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleReportReply(reply.user?.user_id)}
                                  disabled={isCheckingReport || isReporting}
                                >
                                  {isCheckingReport ? "Checking..." : "Report Reply"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="reply-content">
                      {editingReply === reply.id ? (
                        <div className="reply-edit-form">
                          <AddComments
                            postId={postId}
                            commentText={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            addComment={() => {}} 
                            showEditor={true}
                            isEdit={true}
                            initialText={editText}
                            onSaveEdit={saveEditedReply}
                            onInputFocus={() => {}}
                            onCancel={cancelEditingReply}
                            disableMediaEditing={true}
                          />
                        </div>
                      ) : (
                        <div className="reply-text">
                          <PostContent content={reply.content} className="reply-post-content" />
                          {(reply.gif_url || reply.image_url) && (
                          <div className="reply-media-container" style={{
                            marginTop: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            padding: 8,
                            borderRadius: 8
                          }}>
                            {reply.gif_url && (
                              <div className="reply-media reply-gif" onClick={() => setMediaModal({ open: true, media: reply.gif_url, type: 'gif' })} style={{ cursor: 'pointer' }}>
                                <img
                                  src={reply.gif_url}
                                  alt="reply gif"
                                  style={{
                                    width: '100%',
                                    maxWidth: 200,
                                    maxHeight: 200,
                                    objectFit: 'contain',
                                    borderRadius: 8,
                                  }}
                                />
                              </div>
                            )}
                            {reply.image_url && (
                              <div className="reply-media reply-image" onClick={() => setMediaModal({ open: true, media: reply.image_url, type: 'image' })} style={{ cursor: 'pointer' }}>
                                <img
                                  src={reply.image_url}
                                  alt="reply attachment"
                                  style={{
                                    maxWidth: 200,
                                    maxHeight: 200,
                                    objectFit: 'contain',
                                    borderRadius: 8,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        </div>
                      )}
                      {editingReply !== reply.id && (
                        <div className="reply-actions">
                        <button
                          className="reply-like-btn"
                          onClick={() => toggleLikeReply(reply.id, reply.user_has_liked)}
                        >
                          {reply.user_has_liked ? (
                            <FaHeart style={{ fill: "#e91e63" }} />
                          ) : (
                            <FaRegHeart />
                          )}
                          <span className="like-count">
                            {reply.likes_count > 0 ? reply.likes_count : ""}
                          </span>
                        </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {hasMoreReplies && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  className="load-more-replies-btn"
                  onClick={loadMoreReplies}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading...' : 'Load more replies'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="no-replies">No replies yet.</p>
        )}
        {!isCommentingBlocked && (
          <AddComments
            postId={postId}
            commentText={replyText}
            onChange={e => setReplyText(e.target.value)}
            addComment={handleSubmit}
            showEditor={!!showEditor[commentId]}
            onInputFocus={() => handleInputFocus(commentId)}
            onCancel={() => handleCancel(commentId)}
          />
        )}
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
            autoClose={3000}
          />
        )}
      </div>
      <ReportDialog
        isOpen={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        onSubmit={handleReportSubmit}
        isSubmitting={isReporting}
        type="reply"
      />
      {/* Media Modal for full screen view */}
      {mediaModal.open && (
        <div className="media-overlay" onClick={() => setMediaModal({ open: false, media: null, type: null })}>
          <div className="overlay-background"></div>
          <div className="expanded-media-container" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
            <button
              className="close-button"
              style={{ position: 'fixed', top: 24, right: 32, zIndex: 1001, fontSize: 36, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setMediaModal({ open: false, media: null, type: null })}
              aria-label="Close fullscreen media"
            >
              ×
            </button>
            <div className="expanded-media-wrapper">
              {mediaModal.type === 'image' && (
                <img src={mediaModal.media} alt="" className="expanded-media" />
              )}
              {mediaModal.type === 'gif' && (
                <img src={mediaModal.media} alt="" className="expanded-media" />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReplySection;